import type {
  DetectedIssueAttributes,
  PlatformFeedbackAttributes,
  SessionAttributes,
  UserAttributes,
} from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { DetectedIssueType, DetectedIssueUrgency } from '@/client/services/moderation'
import {
  getDetectedIssueTypesWithUrgencies,
  type SafetyReportInternalType,
  SafetyReportStatus,
  type SafetyReportType,
} from '@/client/services/safetyReport'

import ContentFlagQ from './ContentFlagQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import DetectedIssueQ from './DetectedIssueQ'
import SessionQ from './SessionQ'
import UserQ from './UserQ'

const PlatformFeedbackQ = {
  ...makeQueryModelV2<PlatformFeedbackAttributes>('platform_feedback')([
    'id',
    'user_id',
    'type',
    'detected_issue_type',
    'link',
    'description',
    'created_at',
    'session_id',
    'content_flag_id',
    'subject_user_ids',
    'safety_actions',
    'internal_note',
    'internal_types',
    'action_needed_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: { query: SessionQ, foreignKey: SessionQ.id, sourceKey: PlatformFeedbackQ.session_id },
    ContentFlag: {
      query: ContentFlagQ,
      foreignKey: ContentFlagQ.id,
      sourceKey: PlatformFeedbackQ.content_flag_id,
    },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where({
      id: PlatformFeedbackQ.user_id,
    })}`,

  getSession: (): SQLFragment<SessionAttributes> =>
    SessionQ.where({ id: PlatformFeedbackQ.session_id }),

  getActionUserId: (): SQLFragment<number> => SQL`
        (SELECT (
            SELECT CAST(action->>'userId' AS INT) AS user_id FROM jsonb_array_elements(${PlatformFeedbackQ.safety_actions}) AS action
                WHERE action->>'status' = ${SafetyReportStatus.Resolved} OR action->>'status' = ${SafetyReportStatus.InProgress}
                ORDER BY action->>'timestamp' DESC
            LIMIT 1
        ))`,

  getActionUser: (): SQLFragment<UserAttributes> =>
    SQL`FROM ${UserQ} WHERE ${UserQ.id} = ${PlatformFeedbackQ.getActionUserId()}`,

  getSubjectUsers: (): SQLFragment<UserAttributes> => SQL`FROM ${UserQ}
        JOIN (SELECT jsonb_array_elements(${PlatformFeedbackQ.subject_user_ids}) AS user_id)
        AS user_ids ON ${UserQ.id} = CAST(user_id AS INT)`,

  hasSubjectUser: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    ${PlatformFeedbackQ.subject_user_ids} @> jsonb_build_array(${userId})
  `,

  hasInternalType: (type: SafetyReportInternalType): SQLFragment<SafetyReportStatus> => SQL`
    (${PlatformFeedbackQ.internal_types} @> jsonb_build_array(${type}::text))`,

  hasDetectedIssueType: (type: DetectedIssueType): SQLFragment<unknown> => SQL`
    ${PlatformFeedbackQ.detected_issue_type} = ${type}`,

  getStatus: (): SQLFragment<SafetyReportStatus> => SQL`
        CASE
            WHEN jsonb_array_length(${PlatformFeedbackQ.safety_actions}) = 0 THEN ${SafetyReportStatus.NeedsAction}
            ELSE ${PlatformFeedbackQ.safety_actions}->-1->>'status'
        END`,

  // Get all detected issues for this user and the same issue type
  getAllRelatedDetectedIssues: (): SQLFragment<DetectedIssueAttributes[]> =>
    DetectedIssueQ.where(
      {
        type: PlatformFeedbackQ.detected_issue_type,
        order: [[DetectedIssueQ.created_at, 'asc']],
        include: [
          {
            association: 'User',
            required: false,
          },
          {
            association: 'Dispute',
            required: false,
          },
        ],
      },
      SQL`${DetectedIssueQ.user_id} IN (SELECT jsonb_array_elements(${PlatformFeedbackQ.subject_user_ids})::INT)`
    ),

  getMostRecentRelatedDetectedIssue: (): SQLFragment<DetectedIssueAttributes> =>
    DetectedIssueQ.where(
      {
        type: PlatformFeedbackQ.detected_issue_type,
        order: [[DetectedIssueQ.created_at, 'desc']],
        limit: 1,
      },
      SQL`${DetectedIssueQ.user_id} IN (SELECT jsonb_array_elements(${PlatformFeedbackQ.subject_user_ids})::INT)`
    ),

  hasStatus: (status: SafetyReportStatus): SQLFragment<SafetyReportStatus> =>
    SQL`${PlatformFeedbackQ.getStatus()} = ${status}`,

  where: (
    {
      id,
      sessionId,
      userId,
      contentFlagId,
      detectedIssueType,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null
      sessionId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      contentFlagId?: number | null | SQLFragment<number>
      detectedIssueType?: DetectedIssueType | null | SQLFragment<DetectedIssueType>
      include?: IncludeAssociations
      includeSession?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<PlatformFeedbackAttributes> => SQL`
            FROM ${PlatformFeedbackQ}
            ${includeAssociationsToSql(PlatformFeedbackQ, include)}
            WHERE
                ${id != null ? SQL`${PlatformFeedbackQ.id} = ${id} AND` : SQL``}
                ${
                  sessionId != null
                    ? SQL`${PlatformFeedbackQ.session_id} = ${sessionId} AND`
                    : SQL``
                }
                ${userId != null ? SQL`${PlatformFeedbackQ.user_id} = ${userId} AND` : SQL``}
                ${
                  contentFlagId != null
                    ? SQL`${PlatformFeedbackQ.content_flag_id} = ${contentFlagId} AND`
                    : SQL``
                }
                ${
                  detectedIssueType != null
                    ? SQL`${PlatformFeedbackQ.detected_issue_type} = ${detectedIssueType} AND`
                    : SQL``
                }
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,

  matchesTypes: ({
    types,
    internalTypes,
  }: {
    types: SafetyReportType[]
    internalTypes: SafetyReportInternalType[]
  }): SQLFragment<boolean> => SQL`
    (CASE WHEN
        jsonb_array_length(${PlatformFeedbackQ.internal_types}) = 0
      THEN ${PlatformFeedbackQ.type} = ANY(${types})
      ELSE JSONB_EXISTS_ANY(${PlatformFeedbackQ.internal_types}, ${internalTypes})
    END)
  `,

  hasUrgencies: (urgencies: DetectedIssueUrgency[]): SQLFragment<boolean> => SQL`
  ${PlatformFeedbackQ.detected_issue_type} = ANY(${getDetectedIssueTypesWithUrgencies(urgencies)})
`,
}

export default PlatformFeedbackQ
